<template>
  <div class="space-y-4">
    <h2>{{ t('rosSettings') }}</h2>
    <form class="space-y-4" @submit.prevent="onSubmit">
      <SelectField
        name="showHandoverSurvey"
        :label="t('showHandoverSurvey')"
        :options="surveyOptions"
        empty-value-to="null"
      />

      <div class="flex justify-end">
        <CVButton :is-loading="isUpdating" type="submit">{{ t('save') }}</CVButton>
      </div>
    </form>

    <Divider />

    <template v-if="$can('manage', 'ApiKey')">
      <GlobalApiKeysSection />
      <Divider />
    </template>

    <h2>{{ t('more') }}</h2>
    <div class="space-y-4">
      <Select
        v-model="surveyToDownloadId"
        class="col-span-2"
        :label="t('downloadSurveyResultsCsv')"
        :options="surveyOptions"
        empty-value-to="null"
      />
      <div class="flex justify-end">
        <CVButton
          :is-loading="isDownloadingCsv"
          :disabled="!surveyToDownloadId"
          @click.prevent="downloadCsv"
          >{{ t('downloadCsv') }}</CVButton
        >
      </div>
    </div>
    <Divider />

    <div v-if="$can('reload', 'FeatureFlags')" class="space-y-4">
      <h2>{{ t('reloadFeatureFlags') }}</h2>
      <CVButton
        class="w-full"
        :is-loading="reloadingFeatureFlags"
        @click.prevent="reloadFeatureFlags"
        >{{ t('reloadFeatureFlags') }}</CVButton
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import Divider from '@/components/Divider.vue';
import Select from '@/components/Select.vue';
import SelectField from '@/components/SelectField.vue';
import type { GlobalSettings, UpdateGlobalSettingsDto } from '@/entities/global-settings.entity';
import type { Survey } from '@/entities/surveys/survey.entity';
import { useI18n } from 'vue-i18n';
import { useExportSurveyCsv } from '@/queries/use-surveys';
import { downloadBlob } from '@/utils/download-file';
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useReloadFeatureFlags } from '@/queries/use-root';
import GlobalApiKeysSection from './GlobalApiKeysSection.vue';

const props = defineProps<{
  globalSettings: GlobalSettings;
  surveys: Survey[];
  isUpdating: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdateGlobalSettingsDto): void;
}>();

const { t, locale } = useI18n();

const surveyToDownloadId = ref<string | null>(null);

const { handleSubmit } = useForm<UpdateGlobalSettingsDto>({
  initialValues: props.globalSettings,
});

const { mutateAsync: exportCsv, isPending: isDownloadingCsv } = useExportSurveyCsv();

const onSubmit = handleSubmit((values) => {
  emit('onSubmit', values);
});

const surveyOptions = computed(() => {
  return [
    {
      label: t('noSurvey'),
      value: null,
    },
    ...props.surveys.map((survey) => ({
      label: locale.value === 'de' ? survey.titleDe : survey.titleEn,
      value: survey.id,
    })),
  ];
});

const downloadCsv = async () => {
  if (!surveyToDownloadId.value) return;
  const csv = await exportCsv({ surveyId: surveyToDownloadId.value });
  const survey = props.surveys.find((survey) => survey.id === surveyToDownloadId.value);
  const dateString = new Date().toISOString().split('T')[0];
  const fileName =
    (survey ? (locale.value === 'de' ? survey.titleDe : survey.titleEn) : 'Survey') +
    ' ' +
    dateString +
    '.csv';
  await downloadBlob(
    csv instanceof Blob ? csv : new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    fileName,
  );
  surveyToDownloadId.value = null;
};

const { mutateAsync: reloadFeatureFlagsAsync, isPending: reloadingFeatureFlags } =
  useReloadFeatureFlags();

const reloadFeatureFlags = () => {
  reloadFeatureFlagsAsync();
};
</script>

<i18n lang="json">
{
  "en": {
    "rosSettings": "ROS Settings",
    "showHandoverSurvey": "Show handover survey",
    "noSurvey": "- No survey -",
    "downloadSurveyResultsCsv": "Donwload survey results as CSV",
    "downloadCsv": "Download CSV",
    "more": "More",
    "reloadFeatureFlags": "Reload feature flags in API"
  },
  "de": {
    "rosSettings": "ROS Einstellungen",
    "showHandoverSurvey": "Zeige Umfrage bei Übergabe an",
    "noSurvey": "- Keine Umfrage -",
    "downloadSurveyResultsCsv": "Umfragenergebnisse als CSV herunterladen",
    "downloadCsv": "CSV herunterladen",
    "more": "Weiteres",
    "reloadFeatureFlags": "Featureflags in API aktualisieren"
  },
  "es": {
    "rosSettings": "Ajustes ROS",
    "showHandoverSurvey": "Mostrar encuesta al finalizar la entrega",
    "noSurvey": "- Sin encuesta -",
    "downloadSurveyResultsCsv": "Descargar resultados de la encuesta en CSV",
    "downloadCsv": "Descargar CSV",
    "more": "Más",
    "reloadFeatureFlags": "Recargar feature flags en la API"
  }
}
</i18n>
